import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import ProductHeader from '../../molecules/product-detail-header';
import ProductRestrictions from '../../molecules/product-detail-restrictions';
import ProductBadge from '../../molecules/product-badge';
import StickyProductAdd from '../../tissues/product-details-sticky-add-to-cart';
import ProductAddToCart from '../../tissues/product-details-add-to-cart';
import ProductAddToCartGift from '../../tissues/product-details-add-gift-card';
import ProductProperties from '../../tissues/product-details-properties';
import { DISCOUNT_PERCENTAGE } from '../../utils/discountPercent';
import FreeGroundShipping from '../../molecules/free-ground-shipping';

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: product-information;
  flex: 1;
`;

const ProductBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: left;
  margin-bottom: 3rem;
  margin-top: -1rem;

  > div {
    margin: 1rem 2rem 0 0;
  }
`;

const ProductInformation = (props) => {
  const [activeVariant, setActiveVariant] = useState(null);
  const [activeVendor, setActiveVendor] = useState(null);
  const [monogramSelected, setMonogramSelected] = useState(false);

  const getInternationalShipping = () => {
    const id = activeVariant?.prices?.[0]?.vendor_id;
    const stockItem = activeVariant?.stock_items?.filter((item) => item.vendor_id === id);
    return !!stockItem?.[0]?.international_shipping;
  };

  const handleOnVariantChange = (variant) => {
    if (variant) {
      const maisonetteStockId = variant.stock_items.find((x) => x.stock_location_name === 'Maisonette Fulfillment');

      const bestMargin = variant.prices.sort((a, b) => (
        (a.price - a.offer_settings.cost_price > b.price - b.offer_settings.cost_price) ? 1 : -1
      ))[0];

      const vendor = maisonetteStockId?.vendor_id ?? bestMargin?.vendor_id;

      setActiveVariant(variant);
      setActiveVendor(vendor);
    } else {
      setActiveVariant(null);
      setActiveVendor(null);
    }
  };

  const isGiftCard = props.product.gift_card;

  const onVendorChange = (vendor) => setActiveVendor(vendor);

  const getLeadTime = () => {
    const monogram = activeVariant?.prices?.[0]?.monogram;
    let monogramLeadTime;

    if (monogram?.monogrammable_only || (monogram && monogramSelected)) {
      monogramLeadTime = monogram?.monogram_lead_time;
    }

    return monogramLeadTime ?? activeVariant?.lead_time;
  };

  const getPrices = () => activeVariant?.prices?.length ? activeVariant.prices[0] : props?.product?.variants?.map((v) => v.prices[0]) ?? [];

  const noOffers = () => props?.product?.variants?.every((v) => v.price === null) ?? true;

  const isOutletProduct = () => {
    const belongsToOutlet = getPrices();
    if (belongsToOutlet) {
      const discountPercent = parseFloat(belongsToOutlet[0]?.['discount_percent']);
      return (discountPercent >= DISCOUNT_PERCENTAGE ? true : false);
    }
  }
  return (
    <ProductInfo key={props?.product?.id}>
      {
        props.product?.trends && props.product?.trends.length > 0 && (
          <ProductBadges>
            {
              props.product.trends.map(
                (badge) => <ProductBadge key={badge.type} badge={badge.value} />
              )
            }
          </ProductBadges>
        )
      }

      <ProductHeader
        product={props.product}
        title={props?.product?.name}
        brand={props?.product?.brand}
        brandSlug={props?.product?.brand_slug}
        prices={getPrices()}
        noOffers={noOffers()}
        variant={activeVariant}
        vendor={activeVendor}
        slug={props?.product?.slug}
        finalSale={activeVariant?.prices?.[0]?.final_sale}
        isGiftCard={isGiftCard}
      />

      <FreeGroundShipping product={props?.product} activeVariant={activeVariant}/>

      {
        !props.isQuickshop && (activeVariant || isGiftCard) && (
          <ProductRestrictions
            leadTime={getLeadTime()}
            importDuties={getInternationalShipping()}
            isGiftCard={isGiftCard}
          />
        )
      }

      {
        isGiftCard ? (
          <ProductAddToCartGift
            product={props.product}
            variant={activeVariant}
            vendor={activeVendor}
            onVariantChange={handleOnVariantChange}
          />
        ) : (
          <ProductAddToCart
            product={props.product}
            position={props.position}
            variant={activeVariant}
            vendor={activeVendor}
            onVariantChange={handleOnVariantChange}
            isQuickshop={props.isQuickshop}
            setMonogramSelected={setMonogramSelected}
            finalSale={!!activeVariant?.prices?.[0]?.final_sale ?? false}
            isGiftCard={isGiftCard}
            meta={props.meta}
            isOutlet={isOutletProduct()}
          />
        )
      }

      {!props.isQuickshop ? (
        <StickyProductAdd
          product={props.product}
          position={props.position}
          variant={activeVariant}
          vendor={activeVendor}
          onVariantChange={handleOnVariantChange}
          setMonogramSelected={setMonogramSelected}
        />
      ) : null}

      {
        !props.isQuickshop && (
          <ProductProperties
            brand={props.product.brand}
            brandDescription={props.product.brand_description}
            description={props.product.description}
            order={99}
            product={props.product}
            variant={activeVariant}
            vendor={activeVendor}
            onVendorChange={onVendorChange}
          />
        )
      }

    </ProductInfo>
  );
};

ProductInformation.defaultProps = {
  isQuickshop: false,
  position: null,
  meta: {}
};

ProductInformation.propTypes = {
  product: PropTypes.object.isRequired,
  isQuickshop: PropTypes.bool,
  position: PropTypes.number,
  meta: PropTypes.object
};

ProductInformation.whyDidYouRender = true;

export default ProductInformation;
